import { Dayjs } from 'dayjs';
import { formatDurationHHmm } from '../../helpers/general.helpers';
import { User } from '../../models/user.models';
import {
  FALLBACK_FOCUS_COLORS,
  FALLBACK_SPORT_COLORS,
  FOCUS_LAYER_COLORS,
  FOCUS_ORDER,
  SPORT_LAYER_COLORS,
} from './training-log.constants';

export const formatSeasonYearLabel = (seasonStart: Dayjs): string => {
  const startYear = seasonStart.year();
  const endYear = startYear + 1;
  return `${startYear}–${endYear} Training Year`;
};

export const formatSeasonDurationHours = (seconds: number): string => {
  return `${formatDurationHHmm(seconds ?? 0)}h`;
};

export const secondsToChartHours = (seconds: number): number => {
  return (seconds ?? 0) / 3600;
};

export const getAthleteDisplayName = (): string => {
  try {
    const raw = window.localStorage.getItem('user');
    if (!raw) return '';
    const user = JSON.parse(raw) as User;
    return `${user.firstName} ${user.lastName}`.trim();
  } catch {
    return '';
  }
};

// Dashboard chart helpers

export type MetricMode = 'duration' | 'distance';

export interface DurationDistanceMetrics {
  duration?: number;
  distance?: number;
}

export const safeZoneArray = (values?: number[]): number[] => {
  if (!values || values.length !== 6) {
    return [0, 0, 0, 0, 0, 0];
  }
  return values;
};

export const normalizeKey = (key: string): string => key.toLowerCase().replace(/[\s_-]+/g, '');

export const getDurationDistanceMetric = (metrics: DurationDistanceMetrics | undefined, mode: MetricMode): number => {
  if (!metrics) {
    return 0;
  }
  return mode === 'duration' ? metrics.duration ?? 0 : metrics.distance ?? 0;
};

export const toChartValue = (rawValue: number, mode: MetricMode): number =>
  mode === 'duration' ? secondsToChartHours(rawValue) : rawValue;

export const formatChartHoursTick = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}:${m.toString().padStart(2, '0')}`;
};

export const formatStackedChartTooltipValue = (value: number, mode: MetricMode, distanceUnit: string): string => {
  if (mode === 'duration') {
    const seconds = Math.round(value * 3600);
    return formatDurationHHmm(seconds);
  }
  return `${value % 1 === 0 ? value : value.toFixed(1)} ${distanceUnit}`;
};

export const formatDistributionRawValue = (rawValue: number, mode: MetricMode, distanceUnit: string): string => {
  if (mode === 'duration') {
    return formatDurationHHmm(rawValue);
  }
  return `${rawValue % 1 === 0 ? rawValue : rawValue.toFixed(1)} ${distanceUnit}`;
};

export const formatDistributionPercent = (percent: number): string => `${percent.toFixed(2)}%`;

export const formatYAxisTick = (value: number, mode: MetricMode, distanceUnit: string): string => {
  if (mode === 'duration') {
    return formatChartHoursTick(value);
  }
  return `${value} ${distanceUnit}`;
};

const BLACK = '#000000';
const WHITE = '#FFFFFF';

const parseHexColor = (hex: string): [number, number, number] | null => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) {
    return null;
  }
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
};

const toHexColor = (r: number, g: number, b: number): string => {
  const channel = (value: number) => Math.round(value).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
};

const mixHexColors = (hexA: string, hexB: string, ratio: number): string => {
  const a = parseHexColor(hexA);
  const b = parseHexColor(hexB);
  if (!a || !b) {
    return hexA;
  }
  const t = Math.min(1, Math.max(0, ratio));
  return toHexColor(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t);
};

export const getSportLayerColor = (sportKey: string, index: number): string => {
  if (SPORT_LAYER_COLORS[sportKey]) {
    return SPORT_LAYER_COLORS[sportKey];
  }
  return FALLBACK_SPORT_COLORS[index % FALLBACK_SPORT_COLORS.length];
};

export const getSportTypeColor = (
  sportKey: string,
  sportIndex: number,
  typeIndex: number,
  typeCount: number
): string => {
  const base = getSportLayerColor(sportKey, sportIndex);
  if (typeCount <= 1) {
    return base;
  }
  const ratio = typeIndex / (typeCount - 1);
  const darkEnd = mixHexColors(base, BLACK, 0.15);
  const lightEnd = mixHexColors(base, WHITE, 0.25);
  return mixHexColors(darkEnd, lightEnd, ratio);
};

export const FOCUS_CONFIGS = FOCUS_ORDER.map((key) => ({
  key,
  label: key,
  color: FOCUS_LAYER_COLORS[key],
}));

export const getFocusColor = (focusKey: string, index = 0): string => {
  const normalized = normalizeKey(focusKey);
  const match = Object.entries(FOCUS_LAYER_COLORS).find(([key]) => normalizeKey(key) === normalized);
  if (match) {
    return match[1];
  }
  return FALLBACK_FOCUS_COLORS[index % FALLBACK_FOCUS_COLORS.length];
};

export const getFocusLabel = (focusKey: string): string => {
  const normalized = normalizeKey(focusKey);
  const match = FOCUS_ORDER.find((key) => normalizeKey(key) === normalized);
  return match ?? focusKey;
};
